import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Slider,
  Button,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { GetTeamsByUserNameInput, Team, User } from "../models/API";
import { getTeamsWithPermissions } from "../graphql/queries";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  parseUserAttributes,
  UserAttributes,
  DEFAULT_SELF_INTRO,
  DEFAULT_TIMEOUT,
} from "./utils/userAttributes";
import { useGlobal } from "../context/GlobalContext";

// Default menu items
const DEFAULT_MENU_ITEMS = [
  "Dashboard",
  "Ask AI",
  "Report",
  "Data Sources",
  "Knowledge",
  "WBR Callouts",
  "Settings",
  "Admin Console",
  "Help",
];

interface SettingsComponentProps {
  isOpen: boolean;
  menuWidth: number;
  currentTeamId: number;
  currentUserName: string;
  userConfig: User | null;
  teamConfig: Team | null;
  effectiveRole: string;
}

export const SettingsComponent: React.FC<SettingsComponentProps> = ({
  isOpen,
  menuWidth,
  currentTeamId,
  currentUserName,
  teamConfig,
  userConfig,
  effectiveRole,
}) => {
  const theme = useTheme();
  const [selfIntro, setSelfIntro] = useState<string>("");
  const [defaultTeamId, setDefaultTeamId] = useState<number>(currentTeamId);
  const [teams, setTeams] = useState<Team[]>([]);
  const [timeoutValue, setTimeoutValue] = useState<number>(DEFAULT_TIMEOUT);
  const [menuOrder, setMenuOrder] = useState<string[]>(DEFAULT_MENU_ITEMS);
  const { setGlobalTimeout, setShowDataSourceSelector, darkMode, setDarkMode } =
    useGlobal();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [cellRefsEnabled, setCellRefsEnabled] = useState<boolean>(true);
  const [dataSourceSelectorEnabled, setDataSourceSelectorEnabled] =
    useState<boolean>(false);
  const [spektrMcpEnabled, setSpektrMcpEnabled] = useState<boolean>(false);
  const [yoyForecastMcpEnabled, setYoyForecastMcpEnabled] =
    useState<boolean>(false);
  const [clfVocMcpEnabled, setClfVocMcpEnabled] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState<boolean>(false);

  const getTeams = useCallback(async () => {
    try {
      const client = generateAuditedClient();
      const input: GetTeamsByUserNameInput = {
        user_name: currentUserName,
      };
      const result = await client.graphql({
        query: getTeamsWithPermissions,
        variables: {
          input: input,
        },
      });
      const teamsData = result.data.getTeamsWithPermissions;
      setTeams(teamsData);

      // Find and set the default team
      const defaultTeam = teamsData.find((team: Team) => team.is_default);
      if (defaultTeam) {
        setDefaultTeamId(defaultTeam.id);
      }
    } catch (err) {
      console.error(err);
    }
  }, [currentUserName]);

  useEffect(() => {
    getTeams();
  }, [getTeams]);

  // Load user attributes including timeout
  useEffect(() => {
    if (userConfig) {
      const attributes = parseUserAttributes(userConfig);
      setSelfIntro(attributes.self_intro || DEFAULT_SELF_INTRO);

      // Load timeout from attributes, default to 300 if not set
      const savedTimeout = attributes.timeout || DEFAULT_TIMEOUT;
      setTimeoutValue(savedTimeout);
      setGlobalTimeout(savedTimeout);

      // Load menu order from attributes, default to DEFAULT_MENU_ITEMS if not set.
      // Prepend any new menu items that aren't in the user's saved order.
      let savedMenuOrder: string[] =
        attributes.menu_order || DEFAULT_MENU_ITEMS;
      const missing = DEFAULT_MENU_ITEMS.filter(
        (item) => !savedMenuOrder.includes(item)
      );
      if (missing.length > 0) {
        savedMenuOrder = [...missing, ...savedMenuOrder];
      }
      setMenuOrder(savedMenuOrder);

      // Load cell refs enabled setting, default to true
      setCellRefsEnabled(attributes.cell_refs_enabled ?? true);

      // Load data source selector setting, default to false
      const savedShowDS = attributes.show_data_source_selector ?? false;
      setDataSourceSelectorEnabled(savedShowDS);
      setShowDataSourceSelector(savedShowDS);

      // Load Spektr MCP (Ads Agent) setting, default to false
      setSpektrMcpEnabled(attributes.spektr_mcp_enabled ?? false);

      // Load YoY Forecast MCP setting, default to false
      setYoyForecastMcpEnabled(attributes.yoy_forecast_mcp_enabled ?? false);

      // Load CLF VOC MCP setting, default to false
      setClfVocMcpEnabled(attributes.clf_voc_mcp_enabled ?? false);
    }
  }, [userConfig, setGlobalTimeout, setShowDataSourceSelector]);

  const handleTeamChange = (newTeamId: number) => {
    setDefaultTeamId(newTeamId);
    setHasChanges(true);
  };

  const handleSelfIntroChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelfIntro(event.target.value);
    setHasChanges(true);
  };

  const handleTimeoutChange = (event: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setTimeoutValue(value);
    setGlobalTimeout(value);
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...menuOrder];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    setMenuOrder(newOrder);
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === menuOrder.length - 1) return;
    const newOrder = [...menuOrder];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    setMenuOrder(newOrder);
    setHasChanges(true);
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const client = generateAuditedClient();

      const currentAttributes = parseUserAttributes(userConfig);
      const updatedAttributes: UserAttributes = {
        ...currentAttributes,
        self_intro: selfIntro,
        timeout: timeoutValue,
        default_team_id: defaultTeamId,
        menu_order: menuOrder,
        cell_refs_enabled: cellRefsEnabled,
        show_data_source_selector: dataSourceSelectorEnabled,
        dark_mode: darkMode,
        spektr_mcp_enabled: spektrMcpEnabled,
        yoy_forecast_mcp_enabled: yoyForecastMcpEnabled,
        clf_voc_mcp_enabled: clfVocMcpEnabled,
      };

      // Call setUserProfile API
      await client.graphql({
        query: `
          mutation SetUserProfile($input: SetUserProfileInput!) {
            setUserProfile(input: $input) {
              id
              attributes
            }
          }
        `,
        variables: {
          input: {
            id: userConfig?.id,
            default_team_id: defaultTeamId,
            attributes: JSON.stringify(updatedAttributes),
          },
        },
      });

      // Refresh teams to get updated is_default values
      await getTeams();

      setRefreshDialogOpen(true);
      setHasChanges(false);
    } catch (err) {
      console.error("Error saving profile:", err);
      setSnackbarMessage("Failed to save profile. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const formatTimeout = (value: number) => {
    if (value < 60) {
      return `${value}s`;
    }
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleRefreshNow = () => {
    window.location.reload();
  };

  const handleRefreshLater = () => {
    setRefreshDialogOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  const userName = userConfig?.user_name || currentUserName;
  const userId = userConfig?.id || "N/A";

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        ml: `${menuWidth}px`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: theme.transitions.create("margin-left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View your profile and personalize how Ask AI works for you.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveProfile}
            disabled={isSaving || !hasChanges}
            sx={{ minWidth: 120 }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, pb: 2 }}>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* User Profile Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" mb={2}>
                    User Profile
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      label="User ID"
                      value={userId}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Your unique user identifier"
                    />

                    <TextField
                      label="User Name"
                      value={userName}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Your display name"
                    />

                    <TextField
                      label="Role / Introduction"
                      value={selfIntro}
                      onChange={handleSelfIntroChange}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="e.g., Finance Manager, Software Engineer, TPM, Data Scientist..."
                      helperText="Briefly describe your role or introduce yourself (optional)"
                    />

                    <TextField
                      select
                      label="Default Team"
                      value={defaultTeamId}
                      onChange={(e) => handleTeamChange(Number(e.target.value))}
                      fullWidth
                      helperText="Select your default team that loads when you sign in"
                    >
                      {teams.map((team) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name} {team.is_default && "(Current Default)"}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </CardContent>
              </Card>

              {/* Ask AI Preferences Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" mb={2}>
                    Ask AI Preferences
                  </Typography>

                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Request Timeout
                      </Typography>
                      <Typography variant="h6" color="primary" mb={2}>
                        {formatTimeout(timeoutValue)}
                      </Typography>
                      <Slider
                        value={timeoutValue}
                        onChange={handleTimeoutChange}
                        min={30}
                        max={900}
                        step={30}
                        marks={[
                          { value: 30, label: "30s" },
                          { value: 300, label: "5m" },
                          { value: 600, label: "10m" },
                          { value: 900, label: "15m" },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatTimeout}
                        sx={{
                          "& .MuiSlider-markLabel": {
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mt={1}
                      >
                        Maximum time to wait for Ask AI responses (30 seconds to
                        15 minutes)
                      </Typography>
                    </Box>

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={dataSourceSelectorEnabled}
                            onChange={(e) => {
                              setDataSourceSelectorEnabled(e.target.checked);
                              setShowDataSourceSelector(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="Data Source Selector"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        When enabled, a data source selector will appear in the
                        chat input area, allowing you to choose which tools are
                        available for Ask AI queries.
                      </Typography>
                    </Box>

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={spektrMcpEnabled}
                            onChange={(e) => {
                              setSpektrMcpEnabled(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="Spektr MCP (Ads Agent)"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        When enabled, Ask AI can use the Spektr MCP tools from
                        the Ads Agent Fabric gateway (such as advertising data
                        analysis) to answer your questions. Leave off if you
                        don't need ads-agent capabilities.
                      </Typography>
                    </Box>

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={yoyForecastMcpEnabled}
                            onChange={(e) => {
                              setYoyForecastMcpEnabled(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="YoY Forecast MCP"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        When enabled, Ask AI can use the YoY Forecast MCP tools
                        to forecast time series data based on historical YoY
                        growth rates. Leave off if you don't need forecasting
                        capabilities.
                      </Typography>
                    </Box>

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clfVocMcpEnabled}
                            onChange={(e) => {
                              setClfVocMcpEnabled(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="CLF VOC MCP (Voice of Customer)"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        When enabled, Ask AI can use the CLF VOC MCP tools to
                        surface advertiser Voice-of-Customer feedback insights
                        from the CLF semantic layer. Leave off if you don't need
                        customer-feedback insights.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Appearance Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" mb={2}>
                    Appearance
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={darkMode}
                            onChange={(e) => {
                              setDarkMode(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="Dark Mode"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        Switch the entire interface between light and dark
                        themes. Your preference is saved when you click Save.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* WBR Callouts Preferences Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" mb={2}>
                    WBR Callouts
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={cellRefsEnabled}
                            onChange={(e) => {
                              setCellRefsEnabled(e.target.checked);
                              setHasChanges(true);
                            }}
                          />
                        }
                        label="Cell Reference Highlights"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ ml: 7 }}
                      >
                        When enabled, WBR reports will show highlighted cell
                        references by default. You can still toggle highlights
                        on or off per report in the viewer toolbar.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            {/* Menu Order Card */}
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Menu Order
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Customize the order of menu items in the navigation sidebar
                </Typography>

                <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
                  {menuOrder.map((item, index) => (
                    <ListItem
                      key={item}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        "&:last-child": { mb: 0 },
                      }}
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            aria-label="move up"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            size="small"
                          >
                            <ArrowUpwardIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="move down"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === menuOrder.length - 1}
                            size="small"
                          >
                            <ArrowDownwardIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <DragIndicatorIcon
                        sx={{ mr: 2, color: "text.secondary" }}
                      />
                      <ListItemText
                        primary={item}
                        secondary={`Position ${index + 1}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Refresh Dialog */}
      <Dialog
        open={refreshDialogOpen}
        onClose={handleRefreshLater}
        aria-labelledby="refresh-dialog-title"
        aria-describedby="refresh-dialog-description"
      >
        <DialogTitle id="refresh-dialog-title">
          Settings Saved Successfully
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="refresh-dialog-description">
            Your settings have been saved. Please refresh the page for the
            changes to take effect.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefreshLater} color="inherit">
            Refresh Later
          </Button>
          <Button onClick={handleRefreshNow} variant="contained" autoFocus>
            Refresh Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsComponent;
